import React, { useState, useEffect } from 'react';
import { Button, OverlayTrigger, Tooltip, Modal, Form, Row, Col, Tab, Tabs, Spinner } from 'react-bootstrap';
import { IcoEdit, IcoCanc, Lens } from '@icons';
import { editProceduralProgress, deleteProceduralProgress } from '@lib/api';
import Select from 'react-select';
import moment from 'moment';
import UploadDocument from 'components/form/upload/uploadDocuments';
import StatusBar from '@components/StatusBar';
import { translate } from 'components/translation/translation';
import { OETrans } from 'components/translation/OETrans';

const ActionBtnRendererAdvancement = ({ data, setRefresh, optionsProcedureProgesses, canEdit }) => {
	const id = data.id;
	const [editProcedureProgress, setEditProcedureProgress] = useState({});
	const [selectedProcedureProgress, setSelectedProcedureProgress] = useState([]);
	const [showModalEdit, setShowModalEdit] = useState(false);
	const [showModalDelete, setShowModalDelete] = useState(false);
	const [sbShow, setSbShow] = useState(<></>);
	const [activeKey, setActiveKey] = useState('registry');
	const [showSpinner, setShowSpinner] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (id) {
			setEditProcedureProgress(data);
		}
	}, [data, id]);

	useEffect(() => {
		if (id && editProcedureProgress.noticeProceduralProgressId) {
			setSelectedProcedureProgress(
				optionsProcedureProgesses.filter((option) => option.value === editProcedureProgress.noticeProceduralProgressId),
			);
		}
	}, [editProcedureProgress.noticeProceduralProgressId, id, optionsProcedureProgesses]);

	const handleCloseModal = (setShowModal) => {
		setSbShow(<></>);
		setEditProcedureProgress(data);
		setActiveKey('registry');
		setShowModal(false);
	};
	const handleShowModal = (setShowModal) => setShowModal(true);

	const handleStatusBar = (status, message, timeout) => {
		setSbShow(<StatusBar status={status} message={message} timeout={timeout} />);
	};

	const handleDelete = async () => {
		const deleteResult = await deleteProceduralProgress(data?.projectId, id);

		if (deleteResult.success) {
			handleStatusBar('success', translate('common.message.deleted', 'Elemento eliminato correttamente'), true);
			setTimeout(() => {
				setShowModalDelete(false);
				setRefresh(true);
			}, 2000);
		} else {
			handleStatusBar(
				'error',
				deleteResult.message ?? translate('common.error.delete', 'Si è verificato un errore durante l’eliminazione delle informazioni'),
				false,
			);
		}
	};
	const onChange = (e, fieldName, setSelect) => {
		let tempNewProceduralProgress = { ...editProcedureProgress };
		let value;

		switch (fieldName) {
			case 'noticeProceduralProgressId':
				value = e.value;
				tempNewProceduralProgress[fieldName] = value;
				setSelect(e);
				break;
			case 'forecastStartDate':
			case 'forecastEndDate':
			case 'startDate':
			case 'endDate':
			case 'note':
			default:
				value = e.target.value;
				tempNewProceduralProgress[fieldName] = value;
		}
		setEditProcedureProgress(tempNewProceduralProgress);
	};

	const handleEdit = async (e) => {
		e?.preventDefault();

		setSbShow(<></>);

		const { createdAt, createdBy, updatedAt, updatedBy, noticeProceduralProgressId, ...restProceduralProgress } = editProcedureProgress;
		let startDate = editProcedureProgress.startDate;
		let endDate = editProcedureProgress.endDate;
		let forecastStartDate = editProcedureProgress.forecastStartDate;
		let forecastEndDate = editProcedureProgress.forecastEndDate;
		let isForecastDateValid;
		let isStartDateValid;

		if (forecastStartDate !== null && forecastEndDate !== null) {
			isForecastDateValid = forecastStartDate > forecastEndDate;
		}
		if (startDate !== null && endDate !== null) {
			isStartDateValid = startDate > endDate;
		}

		if (!noticeProceduralProgressId) {
			handleStatusBar('error', translate('common.error.requiredFields', 'Compila i campi obbligatori'), false);
			return;
		}
		if (isForecastDateValid || isStartDateValid) {
			handleStatusBar('error', 'La data di inizio non può essere successiva alla data di fine', false);
			return;
		}

		restProceduralProgress.proceduralProgressId = noticeProceduralProgressId;

		let result;
		setIsLoading(true);
		setShowSpinner(true);
		result = await editProceduralProgress(editProcedureProgress?.projectId, editProcedureProgress?.id, restProceduralProgress);

		if (result.success) {
			handleStatusBar('success', translate('common.message.edited', 'Elemento modificato con successo'), true);

			setTimeout(() => {
				setShowModalEdit(false);
				setRefresh(true);
			}, 2500);
		} else {
			handleStatusBar(
				'error',
				result.success === false
					? Object.values(result.data).join()
					: translate('common.error.save', 'Si è verificato un errore durante il salvataggio delle informazioni'),
				false,
			);
		}

		setIsLoading(false);
		setShowSpinner(false);
	};

	const editTooltip = (props) => (
		<Tooltip {...props}>{canEdit ? <OETrans code="common.tooltip.edit" fallback={'Modifica'} /> : 'Visualizza'}</Tooltip>
	);
	const deleteTooltip = (props) => (
		<Tooltip {...props}>
			<OETrans code="common.tooltip.delete" fallback={'Elimina'} />
		</Tooltip>
	);

	return (
		<div>
			<OverlayTrigger placement="top" overlay={editTooltip}>
				<span>
					<Button variant="link" onClick={() => handleShowModal(setShowModalEdit)}>
						{canEdit ? (
							<IcoEdit className="second-color ico-small edit me-3" />
						) : (
							<Lens className="second-color ico-small edit path me-3" />
						)}
					</Button>
				</span>
			</OverlayTrigger>
			{canEdit ? (
				<OverlayTrigger placement="top" overlay={deleteTooltip}>
					<span>
						<Button variant="link" className="me-0" onClick={() => handleShowModal(setShowModalDelete)}>
							<IcoCanc className="second-color ico-small cancel" />
						</Button>
					</span>
				</OverlayTrigger>
			) : null}
			{showModalEdit && (
				<Modal size="lg" className="modal-with-nav" show={showModalEdit} onHide={() => handleCloseModal(setShowModalEdit)} centered>
					<Modal.Header closeButton>
						<Modal.Title>Modifica Progresso Procedurale</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<Tabs className="mb-4" activeKey={activeKey} onSelect={(k) => setActiveKey(k)} fill>
							<Tab eventKey="registry" title={'Dettagli'}>
								<Form id="form-new-procedural-progress">
									<Row style={{ marginBottom: '40px' }}>
										<Col className="col-6">
											<Form.Group as={Col}>
												<Form.Label>
													Progresso Procedurale
													<span className="required-field">*</span>
												</Form.Label>
												<Select
													components={{
														IndicatorSeparator: () => null,
													}}
													className="react-select-container"
													classNamePrefix="react-select"
													name="noticeProceduralProgressId"
													placeholder={translate('common.placeholder.select', 'Seleziona...')}
													value={selectedProcedureProgress}
													options={optionsProcedureProgesses}
													onChange={(e) => onChange(e, 'noticeProceduralProgressId', setSelectedProcedureProgress)}
													isDisabled={!canEdit || isLoading}
												/>
											</Form.Group>
										</Col>
									</Row>
									<Row style={{ marginBottom: '40px' }}>
										<Col className="col-6">
											<Form.Group as={Col}>
												<Form.Label>Data Prevista Inizio</Form.Label>
												<Form.Control
													type="date"
													name="forecastStartDate"
													value={
														editProcedureProgress?.forecastStartDate
															? moment(editProcedureProgress.forecastStartDate).format('YYYY-MM-DD')
															: ''
													}
													onChange={(e) => onChange(e, 'forecastStartDate')}
													disabled={!canEdit || isLoading}
												/>
											</Form.Group>
										</Col>
										<Col className="col-6">
											<Form.Group as={Col}>
												<Form.Label>Data Prevista Fine</Form.Label>
												<Form.Control
													type="date"
													name="forecastEndDate"
													value={
														editProcedureProgress?.forecastEndDate ? moment(editProcedureProgress.forecastEndDate).format('YYYY-MM-DD') : ''
													}
													onChange={(e) => onChange(e, 'forecastEndDate')}
													disabled={!canEdit || isLoading}
												/>
											</Form.Group>
										</Col>
									</Row>
									<Row style={{ marginBottom: '40px' }}>
										<Col className="col-6">
											<Form.Group as={Col}>
												<Form.Label>Data Effettiva Inizio</Form.Label>
												<Form.Control
													type="date"
													name="startDate"
													value={editProcedureProgress?.startDate ? moment(editProcedureProgress.startDate).format('YYYY-MM-DD') : ''}
													onChange={(e) => onChange(e, 'startDate')}
													disabled={!canEdit || isLoading}
												/>
											</Form.Group>
										</Col>
										<Col className="col-6">
											<Form.Group as={Col}>
												<Form.Label>Data Effettiva Fine</Form.Label>
												<Form.Control
													type="date"
													name="endDate"
													value={editProcedureProgress?.endDate ? moment(editProcedureProgress.endDate).format('YYYY-MM-DD') : ''}
													onChange={(e) => onChange(e, 'endDate')}
													disabled={!canEdit || isLoading}
												/>
											</Form.Group>
										</Col>
									</Row>
									<Row style={{ marginBottom: '40px' }}>
										<Col className="col-12">
											<Form.Group as={Col} className="pe-2">
												<Form.Label className="mb-3">Note</Form.Label>
												<Form.Control
													as="textarea"
													rows={3}
													name="note"
													value={editProcedureProgress?.note ? editProcedureProgress.note : ''}
													onChange={(e) => onChange(e, 'note')}
													disabled={!canEdit || isLoading}
												/>
											</Form.Group>
										</Col>
									</Row>
								</Form>
								{sbShow}
							</Tab>
							<Tab eventKey="documents" title={<OETrans code="common.documents" fallback={'Documenti'} />}>
								<UploadDocument
									entity="projectProceduralProgresses"
									entityId={editProcedureProgress.id}
									disableDelete={!canEdit}
									disableEdit={!canEdit}
								/>
								{sbShow}
							</Tab>
						</Tabs>

						<Row>
							<Col className="col-12">
								<span className="label">
									<span className="required-field">*</span>&nbsp;
									<OETrans code="common.requiredFields" fallback={'Campi obbligatori'} />
								</span>
							</Col>
						</Row>
					</Modal.Body>

					<Modal.Footer>
						{canEdit ? (
							<Button variant="primary" onClick={(e) => handleEdit(e)} disabled={activeKey === 'docs' || isLoading}>
								<OETrans code="common.edit" fallback={'Modifica'} />
								{showSpinner && <Spinner as="span" className="ms-2" animation="border" size="sm" role="status" aria-hidden="true" />}
							</Button>
						) : null}
					</Modal.Footer>
				</Modal>
			)}

			<Modal show={showModalDelete} onHide={() => handleCloseModal(setShowModalDelete)} centered>
				<Modal.Header closeButton>
					<Modal.Title>
						<OETrans code="common.warning" fallback={'Attenzione'} />
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="my-3">
						<span>
							<OETrans code="common.warning.delete" fallback={'Sei sicuro di voler eliminare questo elemento?'} />
						</span>
					</div>
					{sbShow}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="danger" onClick={() => handleDelete()}>
						<OETrans code="common.delete" fallback={'Elimina'} />
					</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default ActionBtnRendererAdvancement;
